import React from 'react';

interface DashedLineProperties {
  color: string;
  width: string;
  height: string;
  dashSpacing: string;
  shape: 'horizontal' | 'vertical';
  animate: boolean;
  animationDelay: number;
  animationColor: string;
  dotsCountValue: number;
}

interface CustomCSSProperties extends React.CSSProperties {
  '--dash-spacing'?: string;
  '--color'?: string;
}

/**
 * A customizable dashed line component with loading dots animation
 * @param {any} param0 - The param0
 * @param {any} param0.color - The color prop
 * @param {any} param0.width - The width prop
 * @param {any} param0.height - The height prop
 * @param {any} param0.dashSpacing - The dashSpacing prop
 * @param {any} param0.shape - The shape prop
 * @param {any} param0.animate - The animate prop
 * @param {any} param0.animationDelay - The animationDelay prop
 * @param {any} param0.animationColor - The animationColor prop
 * @param {any} param0.dotsCountValue - The dotsCountValue prop
 */
function DashedLineComponent({
  color,
  width,
  height,
  dashSpacing,
  shape,
  animate,
  animationDelay,
  animationColor,
  dotsCountValue,
}: DashedLineProperties): JSX.Element {
  const lineStyle: CustomCSSProperties = {
    width,
    height,
    position: 'relative',
    animationDelay: `${animationDelay}`,
    overflow: 'hidden',
    '--dash-spacing': dashSpacing,
    '--color': animationColor,
    ...(shape === 'horizontal'
      ? {
          backgroundImage: `radial-gradient(circle closest-side, ${color} 98%, #0000), linear-gradient(${color} 50%, #0000 0)`,
          backgroundPosition: '0 0, 0 calc(var(--s, 10px) / 2)',
          backgroundSize: `100% ${dashSpacing}, 100% calc(2 * ${dashSpacing})`,
          backgroundRepeat: 'repeat-y',
        }
      : {
          backgroundImage: `radial-gradient(circle closest-side at left ${dashSpacing} top 50%, ${color} calc(100% - 1px), #0000),
                            radial-gradient(circle closest-side at right ${dashSpacing} top 50%, ${color} calc(100% - 1px), #0000),
                            linear-gradient(90deg, #0000 25%, ${color} 0 75%, #0000 0)`,
          backgroundSize: `calc(4 * ${dashSpacing}) 100%`,
          backgroundRepeat: 'space no-repeat',
        }),
  };

  const dotsCount = dotsCountValue;
  const dots = animate
    ? Array.from({ length: dotsCount }).map((_, index) => {
        const dotStyle: React.CSSProperties = {
          position: 'absolute',
          borderRadius: '5px',
          backgroundColor: '#d3d3d3',
          animation: 'highlight 5s infinite ease-in-out',
          animationDelay: `${index * 0.4}s`,
        };

        if (shape === 'vertical') {
          dotStyle.width = '15px';
          dotStyle.height = '5px';
          dotStyle.left = `${(index * 100) / (dotsCount - 1)}%`;
          dotStyle.top = '50%';
          dotStyle.transform = 'translate(-50%, -50%)';
        } else {
          dotStyle.width = '5px';
          dotStyle.height = '15px';
          dotStyle.top = `${(index * 100) / (dotsCount - 1)}%`;
          dotStyle.left = '50%';
          dotStyle.transform = 'translate(-50%, -50%)';
        }

        return (
          <div
            key={`dot-${shape}-${dotsCount}-${color}-${animationDelay}-${index * 9973}`}
            className="dot"
            style={dotStyle}
          />
        );
      })
    : null;

  React.useEffect(() => {
    if (animate) {
      const styleElement = document.createElement('style');
      styleElement.innerHTML = `
        @keyframes highlight {
          0%, 50%, 100% {
            background-color: #d3d3d3;
            opacity: 1;
          }
          1% {
            background-color: var(--color);
            opacity: 0.7;
          }
        }
      `;
      document.head.appendChild(styleElement);
      return () => {
        document.head.removeChild(styleElement);
      };
    }
    return undefined;
  }, [animate]);

  return <div style={lineStyle}>{dots}</div>;
}

export default DashedLineComponent;
